import { useState, useEffect } from "react";
import Navigation from "../components/Navigation";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { FileText, TrendingUp, Target, MessageSquare, Users, Apple, Droplets, Clock } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const menuItems = [
  { icon: FileText, label: "Mental Training Plan", path: "/plans" },
  { icon: TrendingUp, label: "Mental Performance", path: "/performance" },
  { icon: Target, label: "Goals", path: "/goals" },
  { icon: MessageSquare, label: "AI Coach", path: "/ai-coach" },
  { icon: Users, label: "Teams", path: "/teams" }
];

export default function NutritionGuide({ user, onLogout }) {
  const [programs, setPrograms] = useState([]);
  const [selectedProgram, setSelectedProgram] = useState(null);

  useEffect(() => {
    fetchPrograms();
  }, []);

  const fetchPrograms = async () => {
    try {
      const response = await axios.get(`${API}/nutrition`);
      setPrograms(response.data);
      if (response.data.length > 0) {
        setSelectedProgram(response.data[0]);
      }
    } catch (error) {
      toast.error("Failed to load nutrition programs");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-blue-50 to-indigo-100">
      <Navigation user={user} onLogout={onLogout} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-12">
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
            Nutrition Guide
          </h1>
          <p className="text-lg text-gray-600">
            Sport-specific nutrition strategies for optimal performance
          </p>
        </div>

        {/* Sport Selection */}
        <div className="mb-8">
          <Tabs value={selectedProgram?.sport} onValueChange={(sport) => {
            const program = programs.find(p => p.sport === sport);
            setSelectedProgram(program);
          }}>
            <TabsList className="grid w-full grid-cols-2 md:grid-cols-5 gap-2">
              {programs.map((program) => (
                <TabsTrigger
                  key={program.sport}
                  data-testid={`nutrition-tab-${program.sport.toLowerCase().replace(/\s+/g, '-')}`}
                  value={program.sport}
                  className="text-sm"
                >
                  {program.sport}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>

        {selectedProgram && (
          <div className="space-y-8">
            {/* Program Header */}
            <Card className="border-0 shadow-xl bg-gradient-to-r from-green-50 to-emerald-50">
              <CardHeader>
                <div className="flex items-start space-x-4">
                  <Apple className="w-12 h-12 text-green-600" />
                  <div>
                    <CardTitle className="text-2xl text-green-900">{selectedProgram.title}</CardTitle>
                    <CardDescription className="text-base text-green-700">
                      {selectedProgram.description}
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
            </Card>

            {/* Nutrition Guidelines */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Nutrition Guidelines</h2>
              <div className="grid gap-6">
                {selectedProgram.guidelines.map((guideline, index) => (
                  <Card
                    key={index}
                    data-testid={`guideline-card-${index}`}
                    className="border-0 shadow-lg bg-white/90 backdrop-blur-sm"
                  >
                    <CardHeader>
                      <CardTitle className="text-xl text-sky-900">{guideline.phase}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2">
                        {guideline.tips.map((tip, tipIndex) => (
                          <li key={tipIndex} className="flex items-start space-x-3">
                            <span className="w-1.5 h-1.5 bg-sky-600 rounded-full mt-2 flex-shrink-0"></span>
                            <span className="text-gray-700">{tip}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Hydration Tips */}
            <Card className="border-0 shadow-lg bg-blue-50 border-blue-200">
              <CardHeader>
                <div className="flex items-center space-x-3">
                  <Droplets className="w-8 h-8 text-blue-600" />
                  <CardTitle className="text-2xl text-blue-900">Hydration Strategy</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {selectedProgram.hydration_tips.map((tip, index) => (
                    <li key={index} className="flex items-start space-x-3">
                      <span className="w-2 h-2 bg-blue-600 rounded-full mt-2 flex-shrink-0"></span>
                      <span className="text-blue-900 font-medium">{tip}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Meal Timing */}
            <Card className="border-0 shadow-lg bg-purple-50 border-purple-200">
              <CardHeader>
                <div className="flex items-center space-x-3">
                  <Clock className="w-8 h-8 text-purple-600" />
                  <CardTitle className="text-2xl text-purple-900">Meal Timing</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {selectedProgram.meal_timing.map((timing, index) => (
                    <li key={index} className="flex items-start space-x-3">
                      <span className="w-2 h-2 bg-purple-600 rounded-full mt-2 flex-shrink-0"></span>
                      <span className="text-purple-900 font-medium">{timing}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Pro Tips */}
            <Card className="border-0 shadow-lg bg-gradient-to-r from-amber-50 to-orange-50">
              <CardHeader>
                <CardTitle className="text-xl text-amber-900">💡 Pro Tips</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-amber-900">
                  <li className="flex items-start space-x-2">
                    <span>✓</span>
                    <span>Keep a food journal to track how different meals affect your performance</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span>✓</span>
                    <span>Never try new foods on competition day - stick to what you know works</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span>✓</span>
                    <span>Individualize your nutrition - what works for teammates may not work for you</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span>✓</span>
                    <span>Consult a sports nutritionist for personalized guidance</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span>✓</span>
                    <span>Mental performance is linked to nutrition - feed your brain and body right</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}